import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  Space,
  Row,
  Col,
  message,
  Card,
  Switch,
  InputNumber,
  Spin
} from 'antd';
import apiService from "services/api/apiService";
import dayjs from 'dayjs';

const { Option } = Select;
const { TextArea } = Input;

interface OnboardingFormProps {
  process?: any;
  onSuccess: () => void;
  onCancel: () => void;
}

const OnboardingForm: React.FC<OnboardingFormProps> = ({ process, onSuccess, onCancel }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [employees, setEmployees] = useState<any[]>([]);
  const [buddyEmployees, setBuddyEmployees] = useState<any[]>([]);

  useEffect(() => {
    fetchEmployees();
    if (process) {
      form.setFieldsValue({
        ...process,
        start_date: process.start_date ? dayjs(process.start_date) : null,
        orientation_date: process.orientation_date ? dayjs(process.orientation_date) : null,
      });
    }
  }, [process, form]);

  const fetchEmployees = async () => {
  try {
    setEmployeesLoading(true);
    const response = await apiService.getEmployees();
    
    console.log('👥 Employees API response:', response);
    
    if (response && response.success) {
      let employeeData = response.data || [];
      
      // Ensure it's always an array
      if (!Array.isArray(employeeData)) {
        if (employeeData.employees) {
          employeeData = employeeData.employees;
        } else if (employeeData.data) {
          employeeData = employeeData.data;
        } else {
          employeeData = [];
        }
      }
      
      const finalEmployeeData = Array.isArray(employeeData) ? employeeData : [];
      console.log('✅ Final employee data:', finalEmployeeData);
      
      setEmployees(finalEmployeeData);
      setBuddyEmployees(finalEmployeeData);
    } else {
      console.log('❌ No employees data returned or API failed');
      setEmployees([]);
      setBuddyEmployees([]);
    }
  } catch (error) {
    console.error('💥 Failed to fetch employees:', error);
    message.warning('Could not load employees. You can still create the onboarding process.');
    setEmployees([]);
    setBuddyEmployees([]);
  } finally {
    setEmployeesLoading(false);
  }
};

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);
      
      const onboardingData = {
        employee_id: values.employee_id,
        start_date: values.start_date ? values.start_date.format('YYYY-MM-DD') : null,
        onboarding_status: values.onboarding_status || 'pending',
        orientation_completed: values.orientation_completed ? 1 : 0,
        orientation_date: values.orientation_date ? values.orientation_date.format('YYYY-MM-DD') : null,
        documents_submitted: values.documents_submitted ? 1 : 0,
        equipment_assigned: values.equipment_assigned ? 1 : 0,
        system_access_granted: values.system_access_granted ? 1 : 0,
        buddy_assigned_id: values.buddy_assigned_id || null,
        notes: values.notes || null,
        completion_percentage: values.completion_percentage || 0
      };

      console.log('Submitting onboarding data:', onboardingData);

      let response;
      if (process) {
        response = await apiService.updateOnboardingProcess(process.id, onboardingData);
      } else {
        response = await apiService.createOnboardingProcess(onboardingData);
      }

      console.log('API Response:', response);

      if (response && response.success) {
        message.success(process ? 'Onboarding process updated successfully' : 'Onboarding process created successfully');
        form.resetFields();
        onSuccess();
      } else {
        message.error(response?.error || 'Operation failed. Please try again.');
      }
    } catch (error: any) {
      console.error('Onboarding operation error:', error);
      message.error('Failed to save onboarding process. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {employeesLoading && (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin tip="Loading employees..." />
        </div>
      )}
      
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          onboarding_status: 'pending',
          orientation_completed: false,
          documents_submitted: false,
          equipment_assigned: false,
          system_access_granted: false,
          completion_percentage: 0
        }}
      >
        <Card title="Basic Information" style={{ marginBottom: '16px' }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="employee_id"
                label="Employee"
                rules={[{ required: true, message: 'Please select an employee' }]}
              >
                <Select
                  placeholder={employees.length > 0 ? "Select employee" : "No employees available"}
                  showSearch
                  optionFilterProp="children"
                  disabled={!!process || employees.length === 0}
                  loading={employeesLoading}
                >
                  {employees.map(emp => (
                    <Option key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name} - {emp.department || 'No Dept'}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              
              {employees.length === 0 && !employeesLoading && (
                <div style={{ fontSize: '12px', color: '#ff4d4f', marginTop: '-16px', marginBottom: '16px' }}>
                  Note: No employees found. Please add employees first or contact your administrator.
                </div>
              )}
            </Col>
            <Col span={12}>
              <Form.Item
                name="start_date"
                label="Start Date"
                rules={[{ required: true, message: 'Please select start date' }]}
              >
                <DatePicker style={{ width: '100%' }} placeholder="Select start date" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="onboarding_status" label="Status">
                <Select>
                  <Option value="pending">Pending</Option>
                  <Option value="in_progress">In Progress</Option>
                  <Option value="completed">Completed</Option>
                  <Option value="on_hold">On Hold</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="buddy_assigned_id" label="Assign Buddy (Optional)">
                <Select
                  placeholder="Select a buddy"
                  allowClear
                  showSearch
                  optionFilterProp="children"
                >
                  {buddyEmployees.map(emp => (
                    <Option key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name} - {emp.position || 'No Position'}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card title="Onboarding Checklist" style={{ marginBottom: '16px' }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="orientation_completed" valuePropName="checked" label="Orientation Completed">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="orientation_date" label="Orientation Date">
                <DatePicker style={{ width: '100%' }} placeholder="Select orientation date" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="documents_submitted" valuePropName="checked" label="Documents Submitted">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="equipment_assigned" valuePropName="checked" label="Equipment Assigned">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="system_access_granted" valuePropName="checked" label="System Access Granted">
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="completion_percentage" label="Completion Percentage">
                <InputNumber
                  min={0}
                  max={100}
                  style={{ width: '100%' }}
                  addonAfter="%"
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card title="Additional Notes">
          <Form.Item name="notes" label="Notes">
            <TextArea rows={4} placeholder="Enter any additional notes about the onboarding process" />
          </Form.Item>
        </Card>

        <Form.Item style={{ marginTop: '24px' }}>
          <Space>
            <Button type="primary" htmlType="submit" loading={loading}>
              {process ? 'Update Onboarding Process' : 'Create Onboarding Process'}
            </Button>
            <Button onClick={onCancel}>
              Cancel
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
};

export default OnboardingForm;
